package it.neckar.lizergy.model.stumps

import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.financial.quote.ConfiguredOptionality
import it.neckar.lizergy.model.project.ProjectConfiguration
import it.neckar.open.unit.currency.EUR
import it.neckar.uuid.NativeUuid
import kotlinx.serialization.Serializable

@Serializable
data class Documentation(
  override val uuid: NativeUuid,
  override val projectId: ProjectConfiguration.PhotovoltaicsProjectId,
  val kundenNummer: String = "",
  val anredeBrief: String = "",
  val anredeBriefkopf: String = "",
  val vertretung: Vertretung? = null,
  val onlineMonitoringPrice: @EUR Money? = null,
  val mandatsReferenz: String = "",
  /**
   * `null` if no choice is stored; [monitoringAuswahlOrDefault] resolves the start value.
   */
  val monitoringAuswahl: MonitoringAuswahl? = null,
  val ersatzstromAuswahl: ErsatzstromAuswahl? = null,
  /**
   * Additional documentation items that appear in the table of contents under "Sonstiges".
   * These are physical documents that are manually added to the documentation folder.
   * Maximum [MaxAdditionalDocumentationItems] items allowed.
   */
  val additionalDocumentationItems: List<String> = emptyList(),
) : ProjectStump {

  /**
   * A `null` [onlineMonitoring] stands for a project without a current configuration.
   */
  fun monitoringAuswahlOrDefault(onlineMonitoring: ConfiguredOptionality?): MonitoringAuswahl {
    return monitoringAuswahl ?: when (onlineMonitoring) {
      ConfiguredOptionality.Selected -> MonitoringAuswahl.Sunny_Portal
      ConfiguredOptionality.NotSelected, ConfiguredOptionality.Optional, null -> MonitoringAuswahl.Kein_Monitoring
    }
  }

  @Serializable
  enum class MonitoringAuswahl(
    /**
     * `null` keeps the choice out of the data sheets of the documentation PDF.
     */
    val dataSheetPath: String?,
  ) {
    Sunny_Portal("Sunny_Portal.pdf"),
    Kein_Monitoring(null),
    ;

    fun format(): String {
      return when (this) {
        Sunny_Portal -> "Sunny Portal"
        Kein_Monitoring -> "Kein Monitoring"
      }
    }
  }

  @Serializable
  enum class ErsatzstromAuswahl(val dataSheetPath: String) {
    Notstrom_SB("Notstrom_für_SB Storage_+_Sunny_Home_Manager.pdf"),
    Notstrom_SB_Smart_Energy_MIT_NOTSTROMSYSTEM("Notstrom_für_SB_Smart_Energy_(Hybrid)_MIT_NOTSTROMSYSTEM.pdf"),
    Notstrom_SI4("Notstrom_für_SI4.4-6.0H-8.0H_und_Sunny_Home_Manager.pdf"),
    Notstrom_STP_Smart_Energy_MIT_LASTUMSCHALTER("Notstrom_für_STP_Smart_Energy_(Hybrid)_MIT_NOTSTROMSYSTEM+LASTUMSCHALTER.pdf"),
    Notstrom_STP_Smart_Energy_OHNE_LASTUMSCHALTER("Notstrom_für_STP_Smart_Energy_(Hybrid)_MIT_NOTSTROMSYSTEM.pdf"),
    Notstrom_STP_Smart_Energy_OHNE_NOTSTROMSYSTEM("Notstrom_für_STP_Smart_Energy_(Hybrid)_OHNE_NOTSTROMSYSTEM.pdf"),
    ;

    fun format(): String {
      return when (this) {
        Notstrom_SB -> "Notstrom für SB Storage + Sunny Home Manager"
        Notstrom_SB_Smart_Energy_MIT_NOTSTROMSYSTEM -> "Notstrom für SB Smart Energy (Hybrid) mit Notstromsystem"
        Notstrom_SI4 -> "Notstrom für SI4.4-6.0H-8.0H und Sunny Home Manager"
        Notstrom_STP_Smart_Energy_MIT_LASTUMSCHALTER -> "Notstrom für STP Smart Energy (Hybrid) mit Notstromsystem + Lastumschalter"
        Notstrom_STP_Smart_Energy_OHNE_LASTUMSCHALTER -> "Notstrom für STP Smart Energy (Hybrid) mit Notstromsystem"
        Notstrom_STP_Smart_Energy_OHNE_NOTSTROMSYSTEM -> "Notstrom für STP Smart Energy (Hybrid) ohne Notstromsystem"
      }
    }
  }

  @Serializable
  data class Vertretung(
    val anrede: String,
    val firstName: String,
    val lastName: String,
    /**
     * Role with leading article, e.g. "den Geschäftsführer", "die Geschäftsführerin", "den Pfarrer".
     * Rendered as "vertreten durch <rolleMitArtikel>" in the Wartungsvertrag-PDF.
     * Default reproduces the historical hardcoded role for projects created before this field existed.
     */
    val rolleMitArtikel: String = "den Geschäftsführer",
  ) {
    fun format(): String {
      return "${anrede.ifBlank { "Herrn/Frau" }} $firstName $lastName"
    }
  }

  companion object {
    /**
     * Number of additional table-of-contents entries under "Sonstiges" that fit on the documentation page.
     */
    const val MaxAdditionalDocumentationItems: Int = 5
  }
}
